import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useTranslation } from "react-i18next";

import './OperatorAnnouncementModal.css';

const TEXT_MAX = 255;

const toLocalDatetimeValue = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const OperatorAnnouncementModal = ({ isOpen, onClose, onSave, announcement }) => {
    const { t } = useTranslation();
    const isEdit = !!announcement;

    const [text, setText] = useState('');
    const [displayFrom, setDisplayFrom] = useState('');
    const [displayUntil, setDisplayUntil] = useState('');
    const [isEnabled, setIsEnabled] = useState(true);
    const [sortOrder, setSortOrder] = useState(0);

    useEffect(() => {
        if (announcement) {
            setText(announcement.text || '');
            setDisplayFrom(toLocalDatetimeValue(announcement.display_from));
            setDisplayUntil(toLocalDatetimeValue(announcement.display_until));
            setIsEnabled(announcement.is_enabled !== false);
            setSortOrder(announcement.sort_order ?? 0);
        } else {
            setText('');
            setDisplayFrom('');
            setDisplayUntil('');
            setIsEnabled(true);
            setSortOrder(0);
        }
    }, [announcement, isOpen]);

    const handleSave = () => {
        if (!text.trim()) {
            alert(t('operator_announcement.error_text_required'));
            return;
        }
        if (text.length > TEXT_MAX) {
            alert(t('operator_announcement.error_text_too_long'));
            return;
        }
        if (displayFrom && displayUntil && new Date(displayFrom) > new Date(displayUntil)) {
            alert(t('operator_announcement.error_period_invalid'));
            return;
        }

        const payload = {
            text: text.trim(),
            display_from: displayFrom || null,
            display_until: displayUntil || null,
            is_enabled: isEnabled,
            sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        };
        onSave(payload, announcement?.id);
    };

    const counterClass = text.length > TEXT_MAX
        ? 'op-ann-modal-counter op-ann-modal-counter-error'
        : (text.length > TEXT_MAX * 0.8 ? 'op-ann-modal-counter op-ann-modal-counter-warn' : 'op-ann-modal-counter');

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="op-ann-modal"
            overlayClassName="op-ann-modal-overlay"
            contentLabel="Operator Announcement Modal"
        >
            <div className="op-ann-modal-title">
                {isEdit ? t('operator_announcement.edit') : t('operator_announcement.add')}
            </div>
            <div className="clearfix"></div>
            <div className="op-ann-modal-form">
                <div className="op-ann-modal-form-title">{t('operator_announcement.text')}</div>
                <div className="op-ann-modal-form-content">
                    <textarea
                        className="op-ann-modal-form-content-textarea"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        rows={3}
                        maxLength={TEXT_MAX}
                    />
                    <div className={counterClass}>{text.length} / {TEXT_MAX}</div>
                </div>

                <div className="op-ann-modal-form-title">{t('operator_announcement.display_from')}</div>
                <div className="op-ann-modal-form-content">
                    <input
                        className="op-ann-modal-form-content-input"
                        type="datetime-local"
                        value={displayFrom}
                        onChange={e => setDisplayFrom(e.target.value)}
                    />
                    <div className="op-ann-modal-form-hint">{t('operator_announcement.display_from_hint')}</div>
                </div>

                <div className="op-ann-modal-form-title">{t('operator_announcement.display_until')}</div>
                <div className="op-ann-modal-form-content">
                    <input
                        className="op-ann-modal-form-content-input"
                        type="datetime-local"
                        value={displayUntil}
                        onChange={e => setDisplayUntil(e.target.value)}
                    />
                    <div className="op-ann-modal-form-hint">{t('operator_announcement.display_until_hint')}</div>
                </div>

                <div className="op-ann-modal-form-title">{t('operator_announcement.is_enabled')}</div>
                <div className="op-ann-modal-form-content">
                    <label className="op-ann-modal-form-checkbox-label">
                        <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={e => setIsEnabled(e.target.checked)}
                        />
                        <span>{t('operator_announcement.is_enabled_yes')}</span>
                    </label>
                </div>

                <div className="op-ann-modal-form-title">{t('operator_announcement.sort_order')}</div>
                <div className="op-ann-modal-form-content">
                    <input
                        className="op-ann-modal-form-content-input op-ann-modal-form-content-input-narrow"
                        type="number"
                        value={sortOrder}
                        onChange={e => setSortOrder(e.target.value)}
                    />
                    <div className="op-ann-modal-form-hint">{t('operator_announcement.sort_order_hint')}</div>
                </div>
            </div>

            <div className="op-ann-modal-button">
                <button className="op-ann-modal-button-cancel" onClick={onClose}>{t('operator_announcement.cancel')}</button>
                <button className="op-ann-modal-button-save" onClick={handleSave}>{t('operator_announcement.save')}</button>
            </div>
        </Modal>
    );
};

export default OperatorAnnouncementModal;
